// utils/familyActions.js
//
// Who may do what to a family of collections (keepr-api § Sub-collections),
// for the menus that offer it. Render honesty only: the API re-checks every
// one, and its refusals are shown as it words them.

import { hasSubCollections, hierarchyOf, isSubCollection, managesParentOf } from './collectionFamily';
import { canManage, getMyAccess } from './permissions';
import { normalizeId } from './ids';

// The publication states that keep a collection out of any family (keepr-api
// utils/collectionFamily.js NEST_BLOCKED_PUBLICATION).
const NEST_BLOCKED_PUBLICATION = ['submitted', 'changes-requested', 'published', 'retiring'];

/**
 * May this collection be either end of a family at all? Never the personal
 * collection, a keepr archive, an archived collection, or a global one (or
 * one on its way to being one).
 */
export function isNestable(collection) {
	if (!collection) return false;
	if (collection.kind === 'personal' || collection.kind === 'archive') return false;
	if (collection.status === 'archived') return false;
	return !NEST_BLOCKED_PUBLICATION.includes(collection?.publication?.state);
}

/** New sub-collection… — a manager of an ordinary top-level collection. */
export const canAddSubCollection = collection => (
	Boolean(collection?._id) && canManage(collection) && isNestable(collection) && !isSubCollection(collection)
);

// Moving a collection into or out of a family takes MANAGE on both ends
// (keepr-api KPR-1267, owner ruling m03: a collection's managers may do what
// its owner may, except transfer it), and a family keeps one owner.

/** Attach an existing collection… — a manager of an ordinary top-level collection. */
export const canAttachSubCollection = collection => (
	Boolean(collection?._id) && canManage(collection) && isNestable(collection) && !isSubCollection(collection)
);

// The owner's id when the row says it (a list row and a collection read carry
// `ownedBy`); '' when it does not, which the candidates filter does not judge.
const ownerIdOf = collection => normalizeId(collection?.ownedBy ?? collection?.owner) || '';

/**
 * The collections `parent` may take in as a sub-collection, of the ones the
 * caller has listed: ones they manage, with the parent's owner (a family has
 * one; a row that does not say is left to the API), ordinary, top-level,
 * with no sub-collections of their own, never itself. The API decides the
 * rest (card key collisions, the owner).
 */
export function attachCandidates(parent, collections = []) {
	const parentId = `${parent?._id || ''}`;
	const parentOwner = ownerIdOf(parent);
	const parents = new Set((collections || []).map(c => `${c?.parent_id || c?.parent?._id || ''}`).filter(Boolean));
	return (collections || []).filter(c => (
		c?._id
		&& `${c._id}` !== parentId
		&& canManage(c)
		&& (!parentOwner || !ownerIdOf(c) || ownerIdOf(c) === parentOwner)
		&& isNestable(c)
		&& !isSubCollection(c)
		&& !parents.has(`${c._id}`)
	));
}

/**
 * Does the caller manage this sub-collection's PARENT? The owner and an admin
 * do; a manager whose access comes from the parent does (managesParentOf);
 * and so does one the parent's own summary says manages it
 * (`hierarchy.parent.myAccess`, present when the parent is readable).
 */
function managesParent(collection) {
	// A family has one owner: the sub-collection's owner owns the parent.
	if (getMyAccess(collection).isOwner === true || managesParentOf(collection)) return true;
	const there = hierarchyOf(collection)?.parent?.myAccess;
	return Boolean(there) && ['manage', 'owner'].includes(there.role);
}

/** Detach from {parent}… — a manager of the sub-collection AND of its parent. */
export const canDetach = collection => (
	Boolean(collection?._id) && isSubCollection(collection)
	&& (getMyAccess(collection).isAdmin || (canManage(collection) && managesParent(collection)))
);

/**
 * Collection settings' Sub-collections tab: on a collection that has
 * sub-collections, or may take one. Never on a sub-collection — it cannot
 * have any — and never a dead end: a personal, archived or global
 * collection with none gets no tab.
 */
export const showsSubCollectionsTab = collection => (
	hasSubCollections(collection) || canAddSubCollection(collection) || canAttachSubCollection(collection)
);

/** A sub-collection's owner is always its parent's: it is transferred with its parent, never alone. */
export const transferBlockedReason = collection => (
	isSubCollection(collection)
		? 'A sub-collection always has its parent collection\'s owner. Transfer the parent collection to move the whole family.'
		: null
);

export { hasSubCollections };
